import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar, User, CreditCard, MapPin, Phone,
  Printer, XCircle, FileText, Edit,
  ClipboardList, FlaskConical, RefreshCw, Layers, Receipt, Barcode, Hash,
  Zap, Globe, Check, X, ChevronDown, ChevronRight, Folder, Search,
  Package, Ticket, Save, Truck, Building2, Clock, CheckCircle, Activity
} from 'lucide-react';
import { bookingsAPI, labTestsAPI, packagesAPI, couponsAPI, labResultsAPI, contractorLabsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const BookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showManageTestsModal, setShowManageTestsModal] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [showSamplesModal, setShowSamplesModal] = useState(false);
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [samplesByDepartment, setSamplesByDepartment] = useState([]);

  // Customer notes modal
  const [showCustomerNotesModal, setShowCustomerNotesModal] = useState(false);
  const [customerNotes, setCustomerNotes] = useState('');

  // Payment modal state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashAmount, setCashAmount] = useState(0);
  const [cardAmount, setCardAmount] = useState(0);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [forceEditPayment, setForceEditPayment] = useState(false);

  // Manage tests modal state
  const [allTests, setAllTests] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [testSearch, setTestSearch] = useState('');
  const [selectedNewTests, setSelectedNewTests] = useState([]);
  const [selectedNewPackages, setSelectedNewPackages] = useState([]);

  // Bulk update modal state
  const [bulkStatus, setBulkStatus] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedTestsForUpdate, setSelectedTestsForUpdate] = useState([]);

  // Result entry state - inline (not modal)
  const [expandedResultTest, setExpandedResultTest] = useState(null);
  const [expandedResultTestData, setExpandedResultTestData] = useState(null);
  const [showComplexResultModal, setShowComplexResultModal] = useState(false);
  const [resultComponents, setResultComponents] = useState([]);
  const [resultComment, setResultComment] = useState('');
  const [loadingResult, setLoadingResult] = useState(false);
  const [savingResult, setSavingResult] = useState(false);
  const [cultureRows, setCultureRows] = useState([]);
  const [serialEntry, setSerialEntry] = useState([]);
  const [sharedComponents, setSharedComponents] = useState([]);
  const [serialStartTime, setSerialStartTime] = useState('');
  const [richTextHtml, setRichTextHtml] = useState('');
  const [richTextImages, setRichTextImages] = useState([]);
  const [cultureOrganisms, setCultureOrganisms] = useState([]);
  const [cultureAntibiotics, setCultureAntibiotics] = useState([]);

  // Contractor booking state
  const [contractorBooking, setContractorBooking] = useState(null);
  const [updatingDeliveryStatus, setUpdatingDeliveryStatus] = useState(false);

  const isPaid = booking?.payment_status === 'paid';
  const isContractorBooking = booking?.visit_type === 'contractor';

  function showPaymentRequiredToast() {
    toast.error('لا يمكن تنفيذ هذا الإجراء قبل تأكيد الدفع');
  }
  const [testsByCategory, setTestsByCategory] = useState({});

  function getPrimarySampleId(test, fallback = '-') {
    if (test?.sample_id) return test.sample_id;
    const serial = Array.isArray(test?.serial_samples) ? test.serial_samples : [];
    if (serial[0]?.sample_id) return serial[0].sample_id;
    return fallback;
  }

  function applyBookingData(bookingData) {
    setBooking(bookingData);

    // Group tests by department for bulk update and samples
    if (bookingData?.tests) {
      const grouped = {};
      bookingData.tests.forEach(test => {
        // Use department from the lab test, fallback to category name, then 'Other'
        const department = test.lab_test_id?.department ||
          test.lab_test_id?.category_id?.name_en ||
          test.lab_test_id?.category_id?.name_ar ||
          'Other';
        if (!grouped[department]) grouped[department] = [];
        grouped[department].push(test);
      });
      setTestsByCategory(grouped);

      // Build labels from real sample IDs, merging any tests that share a
      // sample_id (same tube = one physical sample = ONE label with N tests).
      const bySampleId = new Map();
      let fallbackCounter = 1;

      function pushIntoSample(key, data) {
        if (!bySampleId.has(key)) {
          bySampleId.set(key, { ...data, tests: [], testCount: 0 });
        }
        const entry = bySampleId.get(key);
        data.tests.forEach((name) => {
          if (name && !entry.tests.includes(name)) entry.tests.push(name);
        });
        entry.testCount = entry.tests.length;
      }

      Object.entries(grouped).forEach(([department, tests]) => {
        tests.forEach((test) => {
          const serialStages = Array.isArray(test?.serial_samples) ? test.serial_samples : [];
          const specimenType = test?.lab_test_id?.specimen_type || '-';
          const tubeType = test?.lab_test_id?.tube_type || '-';
          const testName =
            test?.lab_test_id?.name_ar ||
            test?.lab_test_id?.name_en ||
            test?.package_id?.name_ar ||
            test?.package_id?.name_en ||
            '-';

          if (serialStages.length > 0) {
            serialStages.forEach((stage, stageIdx) => {
              const sampleId = stage?.sample_id || `${bookingData.barcode_no || ''}-${fallbackCounter++}`;
              // Serial stages are intentionally unique even if names collide
              const key = `${sampleId}__serial_${stageIdx}`;
              pushIntoSample(key, {
                id: `sample-${sampleId}-${stageIdx}`,
                sampleId,
                department,
                specimenType,
                tubeType,
                phaseLabel: stage?.label || null,
                tests: [testName],
              });
            });
          } else {
            const sampleId = getPrimarySampleId(test, `${bookingData.barcode_no || ''}-${fallbackCounter++}`);
            pushIntoSample(sampleId, {
              id: `sample-${sampleId}`,
              sampleId,
              department,
              specimenType,
              tubeType,
              phaseLabel: null,
              tests: [testName],
            });
          }
        });
      });
      setSamplesByDepartment(Array.from(bySampleId.values()));
    }
  }

  async function refetchBooking() {
    if (!id || id === 'undefined' || id === 'null') {
      return; // Don't navigate, just return - the page will handle it
    }
    try {
      // Use refreshById to bypass cache after updates
      const response = await bookingsAPI.refreshById(id);
      const bookingData = response.data?.data;
      applyBookingData(bookingData);
    } catch (err) {
      const message = err?.response?.data?.message;
      toast.error(message || 'حدث خطأ في تحميل بيانات الحجز');
      // Don't navigate away on refetch errors - user is already on the page
    }
  }

  useEffect(() => {
    let isCancelled = false;

    async function load() {
      // Check for invalid id before making any API call
      // IMPORTANT: Don't navigate if id is missing - this happens during unmount/navigation
      if (!id || id === 'undefined' || id === 'null') {
        // Just return without navigating - let React Router handle navigation
        return;
      }

      setLoading(true);
      try {
        const response = await bookingsAPI.getById(id);
        if (isCancelled) return;

        const bookingData = response.data?.data;
        applyBookingData(bookingData);

        // If this is a contractor booking, fetch the contractor booking details for delivery status
        if (bookingData?.visit_type === 'contractor') {
          try {
            const contractorResponse = await contractorLabsAPI.getAllBookings({ internal_booking_id: id });
            const contractorData = contractorResponse.data?.data?.items?.[0];
            if (contractorData && !isCancelled) {
              setContractorBooking(contractorData);
            }
          } catch (contractorErr) {
            console.error('Error fetching contractor booking:', contractorErr);
          }
        }
        // Tests/packages will be fetched ONLY when opening manage tests modal (lazy load)
      } catch (err) {
        // If component unmounted, don't do anything
        if (isCancelled) return;

        const status = err?.response?.status;
        const message = err?.response?.data?.message;

        toast.error(message || 'حدث خطأ في تحميل بيانات الحجز');
        // Only navigate away when the booking id is invalid or booking not found
        // AND we're still mounted (not navigating away)
        if ((status === 400 || status === 404) && !isCancelled) {
          navigate('/bookings', { replace: true });
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    }

    load();
    return () => { isCancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const loadCultureMasters = async () => {
      try {
        const [oRes, aRes] = await Promise.all([
          labTestsAPI.getCultureOrganisms(),
          labTestsAPI.getCultureAntibiotics()
        ]);
        setCultureOrganisms(oRes.data?.data || []);
        setCultureAntibiotics(aRes.data?.data || []);
      } catch {
        setCultureOrganisms([]);
        setCultureAntibiotics([]);
      }
    };
    loadCultureMasters();
  }, []);

  // Toggle result entry for a test (inline, not modal)
  const handleToggleResultEntry = async (test) => {
    // If already open for this test, close it
    if (expandedResultTest === test._id || (showComplexResultModal && expandedResultTestData?._id === test._id)) {
      setExpandedResultTest(null);
      setExpandedResultTestData(null);
      setShowComplexResultModal(false);
      setResultComponents([]);
      setResultComment('');
      setCultureRows([]);
      setSerialEntry([]);
      setSharedComponents([]);
      setSerialStartTime('');
      setRichTextHtml('');
      setRichTextImages([]);
      return;
    }

    setLoadingResult(true);
    setExpandedResultTest(null);
    setExpandedResultTestData(test);

    try {
      const response = await labResultsAPI.getDetails(test._id);
      const data = response.data?.data;
      const detailsLabTest = data?.bookingTest?.lab_test_id || {};
      const resolvedLabTest = { ...(test.lab_test_id || {}), ...(detailsLabTest || {}) };
      const isSerialFromDetails = Boolean(resolvedLabTest?.is_serial_test);
      const serialIntervalsFromDetails = Array.isArray(resolvedLabTest?.serial_intervals) ? resolvedLabTest.serial_intervals : [];
      setExpandedResultTestData({ ...test, lab_test_id: resolvedLabTest });
      const isCalculated = data?.bookingTest?.lab_test_id?.is_calculated || false;

      let resolvedComponents = [];
      // Set components with reference ranges
      if (data?.components && data.components.length > 0) {
        resolvedComponents = data.components.map(c => ({
          ...c,
          value: c.value_numeric ?? c.value_text ?? '',
          is_calculated: isCalculated
        }));
        setResultComponents(resolvedComponents);
      } else {
        // Fallback: create a single component for simple tests
        resolvedComponents = [{
          component_id: null,
          component_name: test.lab_test_id?.name_en || 'النتيجة',
          unit: '',
          result_type: 'numeric',
          reference_range: null,
          value: '',
          is_calculated: isCalculated
        }];
        setResultComponents(resolvedComponents);
      }
      setResultComment(data?.result?.result_comment || '');

      setCultureRows(data?.culture_grid_rows || data?.result?.culture_grid_rows || []);
      setRichTextHtml(data?.rich_text_html || data?.result?.rich_text_html || '');
      setRichTextImages(data?.rich_text_images || data?.result?.rich_text_images || []);

      const serialIntervals = serialIntervalsFromDetails;
      const serialSamples = test?.serial_samples || [];
      const existingSerial = data?.serial_results || data?.result?.serial_results || [];
      if (isSerialFromDetails && serialIntervals.length > 0) {
        // data.components has the full component definitions (with options, result_type)
        const baseComponents = data.components || [];

        // Separate shared (info) components from measurement components
        // Info: single_select or text with no reference range (like "Substrate Used", "Total Test Duration")
        // Measurement: numeric with reference range (like H2, CH4, H2S readings)
        const infoComponents = [];
        const measurementComponents = [];
        baseComponents.forEach((comp) => {
          const hasRefRange = comp.reference_range && (comp.reference_range.low_value !== null || comp.reference_range.high_value !== null);
          const isInfoType = (comp.result_type === 'single_select' || comp.result_type === 'text') && !hasRefRange;
          if (isInfoType) {
            // Check if there's an existing value from the first interval
            const firstExisting = existingSerial[0] || {};
            const existingComp = (firstExisting.components || []).find(
              (ec) => String(ec.test_component_id) === String(comp.component_id || comp.test_component_id)
            );
            infoComponents.push({
              test_component_id: comp.component_id || comp.test_component_id,
              component_name: comp.component_name,
              result_type: comp.result_type,
              unit: comp.unit,
              options: comp.options || [],
              reference_range: comp.reference_range || null,
              value: existingComp ? (existingComp.value_numeric ?? existingComp.value_text ?? '') : '',
              abnormal_flag: existingComp?.abnormal_flag || null
            });
          } else {
            measurementComponents.push(comp);
          }
        });

        setSharedComponents(infoComponents);

        const built = serialIntervals.map((label, idx) => {
          const sample = serialSamples[idx] || {};
          const existing = existingSerial.find((r) => r.label === label) || {};
          return {
            label,
            sample_id: existing.sample_id || sample.sample_id || null,
            sample_collected_at: existing.sample_collected_at || '',
            components: measurementComponents.map((comp) => {
              // Find existing value for this component in this interval
              const existingComp = (existing.components || []).find(
                (ec) => String(ec.test_component_id) === String(comp.component_id || comp.test_component_id)
              );
              return {
                test_component_id: comp.component_id || comp.test_component_id,
                component_name: comp.component_name,
                result_type: comp.result_type,
                unit: comp.unit,
                options: comp.options || [],
                reference_range: comp.reference_range || null,
                value: existingComp ? (existingComp.value_numeric ?? existingComp.value_text ?? '') : '',
                abnormal_flag: existingComp?.abnormal_flag || null
              };
            })
          };
        });
        setSerialEntry(built);

        // Try to infer start time from existing data
        const firstWithTime = built.find(r => r.sample_collected_at);
        if (firstWithTime) {
          const mins = parseMinutesFromLabel(firstWithTime.label);
          if (mins !== null) {
            const d = new Date(new Date(firstWithTime.sample_collected_at).getTime() - mins * 60000);
            if (!isNaN(d.getTime())) {
              setSerialStartTime(d.toISOString().slice(0, 16));
            }
          }
        } else {
          setSerialStartTime('');
        }
      } else {
        setSerialEntry([]);
        setSharedComponents([]);
        setSerialStartTime('');
      }

      const hasComplexResultType = resolvedComponents.some((c) => ['culture_grid', 'rich_text_with_images'].includes(c.result_type));
      const isComplex = Boolean(isSerialFromDetails || hasComplexResultType);
      if (isComplex) {
        setShowComplexResultModal(true);
        setExpandedResultTest(null);
      } else {
        setShowComplexResultModal(false);
        setExpandedResultTest(test._id);
      }
    } catch (err) {
      console.error('Error loading result details:', err);
      // Fallback for tests without sections/components
      setResultComponents([{
        component_id: null,
        component_name: test.lab_test_id?.name_en || 'النتيجة',
        unit: '',
        result_type: 'numeric',
        reference_range: null,
        value: '',
        is_calculated: false
      }]);
      setCultureRows([]);
      setSerialEntry([]);
      setSharedComponents([]);
      setSerialStartTime('');
      setRichTextHtml('');
      setRichTextImages([]);
      setShowComplexResultModal(false);
      setExpandedResultTest(test._id);
    }
    setLoadingResult(false);
  };

  // Save result
  const handleSaveResult = async (testId) => {
    if (!testId) return;

    if (expandedResultTestData?.lab_test_id?.is_serial_test && serialEntry.length > 0) {
      const hasAnyValue = serialEntry.some((r) => (r.components || []).some((c) => c.value !== '' && c.value !== null && c.value !== undefined));
      if (!hasAnyValue) {
        toast.error('يرجى إدخال قيمة واحدة على الأقل في أحد فترات السحب');
        return;
      }
    }

    const validComponents = resultComponents.filter(c => c.value !== '' && c.value !== null);
    const hasCultureData = cultureRows.some((r) => r.organism_name && (r.antibiotics || []).length > 0);
    const hasRichTextData = richTextHtml && richTextHtml.trim();
    if (!expandedResultTestData?.lab_test_id?.is_serial_test && validComponents.length === 0 && !hasCultureData && !hasRichTextData) {
      toast.error('يرجى إدخال قيمة واحدة على الأقل');
      return;
    }

    setSavingResult(true);
    try {
      await labResultsAPI.enterResult(testId, {
        components: validComponents.map(c => ({
          test_component_id: c.component_id || undefined,
          value_numeric: c.result_type === 'numeric' ? parseFloat(c.value) : null,
          value_text: c.result_type !== 'numeric' ? c.value : null,
          unit: c.unit || null,
          abnormal_flag: c.abnormal_flag || null
        })),
        serial_results: expandedResultTestData?.lab_test_id?.is_serial_test ? serialEntry.map((row) => {
          // Merge shared (info) components back into each interval for the API
          const sharedMapped = (sharedComponents || [])
            .filter((c) => c.value !== '' && c.value !== null && c.value !== undefined)
            .map((c) => ({
              test_component_id: c.test_component_id,
              value_numeric: c.result_type === 'numeric' ? parseFloat(c.value) : null,
              value_text: c.result_type !== 'numeric' ? c.value : null,
              unit: c.unit || null,
              abnormal_flag: c.abnormal_flag || null
            }));
          const measurementMapped = (row.components || [])
            .filter((c) => c.value !== '' && c.value !== null && c.value !== undefined)
            .map((c) => ({
              test_component_id: c.test_component_id,
              value_numeric: c.result_type === 'numeric' ? parseFloat(c.value) : null,
              value_text: c.result_type !== 'numeric' ? c.value : null,
              unit: c.unit || null,
              abnormal_flag: c.abnormal_flag || null
            }));
          return {
            label: row.label,
            sample_id: row.sample_id || null,
            sample_collected_at: row.sample_collected_at || null,
            components: [...sharedMapped, ...measurementMapped]
          };
        }) : [],
        culture_grid_rows: cultureRows,
        rich_text_html: richTextHtml || null,
        rich_text_images: richTextImages,
        result_comment: resultComment
      });

      toast.success('تم حفظ النتيجة بنجاح');
      setExpandedResultTest(null);
      setExpandedResultTestData(null);
      setShowComplexResultModal(false);
      setResultComponents([]);
      setResultComment('');
      setCultureRows([]);
      setSerialEntry([]);
      setSharedComponents([]);
      setSerialStartTime('');
      setRichTextHtml('');
      setRichTextImages([]);
      refetchBooking(); // Refresh to show updated status
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ في حفظ النتيجة');
    }
    setSavingResult(false);
  };

  // Calculate abnormal flag based on value and reference range
  const calculateAbnormalFlag = (value, refRange) => {
    if (!refRange || value === '' || value === null) return null;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return null;

    if (refRange.low_value !== null && numValue < refRange.low_value) return 'L';
    if (refRange.high_value !== null && numValue > refRange.high_value) return 'H';
    return 'N';
  };

  // Parse minutes from serial interval label (e.g. "Baseline (0 min)" → 0, "15 min" → 15)
  const parseMinutesFromLabel = (label) => {
    const match = label.match(/(\d+)\s*min/i);
    if (match) return parseInt(match[1]);
    if (label.toLowerCase().includes('baseline') || label.toLowerCase().includes('base')) return 0;
    return null;
  };

  // When start time changes, auto-fill all interval sample_collected_at times
  const handleSerialStartTimeChange = (dateStr, timeStr) => {
    const combined = dateStr && timeStr ? `${dateStr}T${timeStr}` : '';
    setSerialStartTime(combined);
    if (!combined) return;
    const startDate = new Date(combined);
    if (isNaN(startDate.getTime())) return;
    setSerialEntry(prev => prev.map(row => {
      const mins = parseMinutesFromLabel(row.label);
      if (mins === null) return row;
      const t = new Date(startDate.getTime() + mins * 60000);
      return { ...row, sample_collected_at: t.toISOString() };
    }));
  };

  // Format a date/time for display in interval headers
  const formatIntervalTime = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('ar-SA-u-ca-gregory', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const getAbnormalLabel = (flag) => {
    switch (flag) {
      case 'H': return { text: 'High', color: 'text-red-600 bg-red-50' };
      case 'L': return { text: 'Low', color: 'text-red-600 bg-red-50' };
      case 'N': return { text: 'Normal', color: 'text-green-600 bg-green-50' };
      default: return null;
    }
  };

  const addCultureRow = () => {
    setCultureRows((prev) => [...prev, { organism_name: '', antibiotics: [] }]);
  };

  const addCultureAntibiotic = (rowIndex) => {
    setCultureRows((prev) => prev.map((row, idx) => (
      idx === rowIndex
        ? { ...row, antibiotics: [...(row.antibiotics || []), { antibiotic_name: '', sensitivity: 'S', mic_value: '' }] }
        : row
    )));
  };

  const onRichImageUpload = async (files) => {
    const mapped = await Promise.all(Array.from(files || []).map((file) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ url: reader.result, caption: file.name });
      reader.readAsDataURL(file);
    })));
    setRichTextImages((prev) => [...prev, ...mapped]);
  };

  const fetchTestsAndPackages = async () => {
    try {
      const [testsRes, packagesRes] = await Promise.all([
        labTestsAPI.getAll({ limit: 500 }),
        packagesAPI.getAll({ limit: 500 })
      ]);
      setAllTests(testsRes.data?.data?.items || []);
      setAllPackages(packagesRes.data?.data?.items || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const getCustomerName = (customer) => {
    if (!customer) return '-';
    // Check for full_name first (as stored in DB), then fallback to first_name + last_name
    if (customer.full_name) return customer.full_name;
    const firstName = customer.first_name || customer.firstName || '';
    const lastName = customer.last_name || customer.lastName || '';
    return `${firstName} ${lastName}`.trim() || '-';
  };

  const calculateAge = (dob) => {
    if (!dob) return '-';
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('الرجاء إدخال كود الخصم');
      return;
    }

    try {
      setApplyingCoupon(true);
      const response = await couponsAPI.getByCode(couponCode.toUpperCase());
      const coupon = response.data?.data;

      if (!coupon) {
        toast.error('كود الخصم غير صحيح');
        return;
      }

      // Calculate discount
      const subtotal = booking?.net_amount || 0;
      let discount = 0;

      if (coupon.type === 'fixed') {
        discount = Math.min(coupon.value, subtotal);
      } else {
        discount = (subtotal * coupon.value) / 100;
      }

      setAppliedCoupon(coupon);
      setDiscountAmount(discount);
      toast.success(`تم تطبيق الخصم بنجاح: ${discount.toFixed(2)} ر.س`);
    } catch (error) {
      console.error('Error applying coupon:', error);
      toast.error(error.response?.data?.message || 'حدث خطأ أثناء تطبيق كود الخصم');
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setCouponCode('');
    toast.success('تم إزالة كود الخصم');
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-amber-100 text-amber-700',
      pending: 'bg-amber-100 text-amber-700',
      sample_collected: 'bg-cyan-100 text-cyan-700',
      collected: 'bg-cyan-100 text-cyan-700',
      partially_collected: 'bg-orange-100 text-orange-700',
      rejected: 'bg-red-100 text-red-700',
      verified: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700',
      approved: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusText = (status) => {
    const texts = {
      new: 'قيد الانتظار',
      pending: 'Pending',
      sample_collected: 'تم جمع العينة',
      collected: 'Collected',
      partially_collected: 'Partially collected',
      rejected: 'Rejected',
      verified: 'Verified',
      in_progress: 'قيد المعالجة',
      completed: 'مكتمل',
      approved: 'Approved',
      cancelled: 'ملغي'
    };
    return texts[status] || status;
  };

  // Cancel modal state
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  // ... (other states)

  const handleCancelBooking = async () => {
    if (!cancelReason.trim()) {
      toast.error('يرجى كتابة سبب الإلغاء');
      return;
    }

    setIsCancelling(true);
    try {
      await bookingsAPI.cancel(id, { reason: cancelReason });
      toast.success('تم إلغاء الحجز بنجاح');
      if (booking?.payment_status === 'paid') {
        toast.success('تم إصدار إشعار دائن (Credit Note) وإرساله للهيئة');
      }
      await refetchBooking();
      setShowCancelModal(false);
      setCancelReason('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء إلغاء الحجز');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleProcessPayment = async () => {
    if (!paymentMethod) {
      toast.error('يرجى اختيار طريقة الدفع');
      return;
    }

    setProcessingPayment(true);
    try {
      // First, apply coupon if exists (backend will recalculate amounts)
      if (appliedCoupon?.code && !booking?.coupon_code) {
        await bookingsAPI.updateStatus(id, {
          coupon_code: appliedCoupon.code
        });
        // Refresh booking to get new amounts
        const refreshedBooking = await bookingsAPI.getById(id);
        setBooking(refreshedBooking.data?.data);
      }

      // Get the latest amounts (after discount)
      const latestBooking = booking?.coupon_code ? booking : (await bookingsAPI.getById(id)).data?.data;
      const finalAmount = latestBooking.net_amount;
      let paidAmount = finalAmount;

      if (paymentMethod === 'cash_card') {
        paidAmount = cashAmount + cardAmount;
        if (paidAmount < finalAmount) {
          toast.error('المبلغ المدفوع أقل من المطلوب');
          setProcessingPayment(false);
          return;
        }
      }

      await bookingsAPI.updateStatus(id, {
        payment_status: 'paid',
        payment_method: paymentMethod,
        paid_amount: paidAmount,
        remaining_amount: 0,
        cash_amount: paymentMethod === 'cash' ? finalAmount : (paymentMethod === 'cash_card' ? cashAmount : 0),
        card_amount: paymentMethod === 'card' ? finalAmount : (paymentMethod === 'cash_card' ? cardAmount : 0)
      });
      toast.success('تم معالجة الدفع بنجاح');
      await refetchBooking();
      setShowPaymentModal(false);
      setPaymentMethod('cash');
      setCashAmount(0);
      setCardAmount(0);
      setAppliedCoupon(null);
      setDiscountAmount(0);
      setCouponCode('');
      setForceEditPayment(false);
    } catch (err) {
      console.error('Payment error:', err);
      toast.error('حدث خطأ أثناء معالجة الدفع');
    }
    setProcessingPayment(false);
  };

  const handleAddTests = async () => {
    toast.success(`تم إضافة ${selectedNewTests.length + selectedNewPackages.length} عناصر`);
    setShowManageTestsModal(false);
    setSelectedNewTests([]);
    setSelectedNewPackages([]);
  };

  const handleBulkUpdate = async () => {
    if (!bulkStatus) {
      toast.error('يرجى اختيار الحالة الجديدة');
      return;
    }
    if (selectedTestsForUpdate.length === 0) {
      toast.error('يرجى اختيار تحاليل للتحديث');
      return;
    }

    try {
      const response = await bookingsAPI.updateTestsStatus(id, {
        testIds: selectedTestsForUpdate,
        status: bulkStatus
      });
      toast.success(`تم تحديث ${response.data?.data?.modifiedCount || selectedTestsForUpdate.length} تحليل بنجاح`);
      await refetchBooking();
      setShowBulkUpdateModal(false);
      setSelectedTestsForUpdate([]);
      setBulkStatus('');
    } catch (err) {
      toast.error('حدث خطأ أثناء تحديث الحالة');
    }
  };

  const handleSaveCustomerNotes = async () => {
    try {
      await bookingsAPI.updateStatus(id, { customer_notes: customerNotes });
      toast.success('تم حفظ الملاحظات بنجاح');
      await refetchBooking();
      setShowCustomerNotesModal(false);
    } catch (err) {
      toast.error('حدث خطأ أثناء حفظ الملاحظات');
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleTestSelection = (testId) => {
    setSelectedTestsForUpdate(prev =>
      prev.includes(testId)
        ? prev.filter(id => id !== testId)
        : [...prev, testId]
    );
  };

  const selectAllVisible = () => {
    const allIds = Object.values(testsByCategory).flat().map(t => t._id);
    setSelectedTestsForUpdate(allIds);
  };

  const clearSelection = () => {
    setSelectedTestsForUpdate([]);
  };

  const handlePrint = (type) => {
    toast.success(`جاري طباعة ${type}...`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">لم يتم العثور على الحجز</p>
      </div>
    );
  }

  const customer = booking.customer_id;
  const filteredTests = allTests.filter(t =>
    t.name_en?.toLowerCase().includes(testSearch.toLowerCase()) ||
    t.name_ar?.includes(testSearch)
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-[#F8FAFC]"
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" dir="rtl">
        {/* Modern Header with Booking Info */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_4px_6px_-1px_rgb(0_0_0_/_0.05)] overflow-hidden mb-6"
        >
          {/* Top Bar */}
          <div className="bg-[#1E293B] px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-slate-100" strokeWidth={2} />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-100 tracking-tight">تفاصيل الحجز</h1>
                  <p className="text-slate-400 text-sm mt-0.5">رقم الحجز: <span className="font-mono text-slate-100">{booking?.booking_no}</span></p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {booking?.visit_type === 'contractor' && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-500/20 text-purple-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                    مختبر متعاقد
                  </span>
                )}
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${booking?.status === 'completed' ? 'bg-emerald-500/20 text-emerald-300' :
                  booking?.status === 'cancelled' ? 'bg-rose-500/20 text-rose-300' :
                    booking?.status === 'online_unconfirmed' ? 'bg-amber-500/20 text-amber-300' :
                    'bg-amber-500/20 text-amber-300'
                  }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${booking?.status === 'completed' ? 'bg-emerald-400' :
                    booking?.status === 'cancelled' ? 'bg-rose-400' :
                      booking?.status === 'online_unconfirmed' ? 'bg-amber-400' :
                      'bg-amber-400'
                    }`} />
                  {booking?.status === 'completed' ? 'مكتمل' :
                    booking?.status === 'cancelled' ? 'ملغي' :
                      booking?.status === 'online_unconfirmed' ? 'عميل ويب غير مؤكد' : 'جديد'}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${booking?.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-300' :
                  booking?.payment_status === 'partial' ? 'bg-amber-500/20 text-amber-300' :
                    booking?.payment_status === 'refunded' ? 'bg-purple-500/20 text-purple-300' :
                      'bg-rose-500/20 text-rose-300'
                  }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${booking?.payment_status === 'paid' ? 'bg-emerald-400' :
                    booking?.payment_status === 'partial' ? 'bg-amber-400' :
                      booking?.payment_status === 'refunded' ? 'bg-purple-400' :
                        'bg-rose-400'
                    }`} />
                  {booking?.payment_status === 'paid' ? 'مدفوع' :
                    booking?.payment_status === 'partial' ? 'جزئي' :
                      booking?.payment_status === 'refunded' ? 'مرتجع' : 'غير مدفوع'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 p-4 bg-slate-50/50">
            <div className="text-center p-3 bg-white rounded-xl border border-slate-100">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">المجموع الفرعي</p>
              <p className="text-xl font-bold text-slate-900 tabular-nums">{booking?.total_amount?.toFixed(2)}</p>
              <p className="text-[10px] text-slate-400">SAR</p>
            </div>
            {booking?.discount_amount > 0 && (
              <div className="text-center p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">بعد الخصم</p>
                <p className="text-xl font-bold text-emerald-600 tabular-nums">
                  {Math.max(0, (booking?.total_amount || 0) - Math.min((booking?.discount_amount || 0), (booking?.total_amount || 0))).toFixed(2)}
                </p>
                <p className="text-[10px] text-emerald-500">{booking?.coupon_code || 'كوبون'}</p>
              </div>
            )}
            <div className="text-center p-3 bg-white rounded-xl border border-slate-100">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">الضريبة ({booking?.vat_amount > 0 ? '15%' : '0%'})</p>
              <p className="text-xl font-bold text-amber-600 tabular-nums">{Math.max(0, booking?.vat_amount || 0).toFixed(2)}</p>
              <p className="text-[10px] text-slate-400">SAR</p>
            </div>
            <div className="text-center p-3 bg-[#1E293B] rounded-xl">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">المطلوب</p>
              <p className="text-xl font-bold text-slate-100 tabular-nums">{Math.max(0, booking?.net_amount || 0).toFixed(2)}</p>
              <p className="text-[10px] text-slate-400">SAR</p>
            </div>
            <div className="text-center p-3 bg-white rounded-xl border border-slate-100">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">المدفوع</p>
              <p className="text-xl font-bold text-slate-900 tabular-nums">{booking?.paid_amount?.toFixed(2) || '0.00'}</p>
              <p className="text-[10px] text-slate-400">SAR</p>
            </div>
            <div className="text-center p-3 bg-white rounded-xl border border-slate-100">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">المتبقي</p>
              <p className="text-xl font-bold text-rose-500 tabular-nums">{Math.max(0, booking?.remaining_amount || 0).toFixed(2)}</p>
              <p className="text-[10px] text-slate-400">SAR</p>
            </div>
            {booking?.payment_status === 'refunded' && (
              <div className="text-center p-3 bg-purple-50 rounded-xl border border-purple-100">
                <p className="text-[11px] font-semibold text-purple-500 uppercase tracking-wider mb-1">المبلغ المرتجع</p>
                <p className="text-xl font-bold text-purple-700 tabular-nums">
                  {(booking?.paid_amount || 0).toFixed(2)}
                </p>
                <p className="text-[10px] text-purple-400">SAR</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Quick Actions - Modern Card Style */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] p-5 mb-6"
        >
          <h2 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Zap className="w-4 h-4 text-slate-600" strokeWidth={2} />
            الإجراءات السريعة
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {booking?.visit_type === 'contractor' ? (
              <div
                className="group p-3 bg-slate-100 text-slate-400 rounded-xl font-medium flex flex-col items-center gap-1.5 cursor-not-allowed"
                title="حجوزات المختبرات المتعاقدة مدفوعة تلقائياً"
              >
                <CreditCard className="w-5 h-5" strokeWidth={2} />
                <span className="text-xs">مدفوع (متعاقد)</span>
              </div>
            ) : (
              <button
                onClick={() => {
                  if (booking?.payment_status === 'paid') {
                    toast('الفاتورة مدفوعة - لا يمكن التعديل بعد الإصدار (ZATCA)', { icon: '⚠️' });
                    return;
                  }
                  setShowPaymentModal(true);
                }}
                disabled={booking?.status === 'cancelled' || booking?.payment_status === 'paid'}
                className={`group p-3 rounded-xl font-medium flex flex-col items-center gap-1.5 transition-all ${booking?.status === 'cancelled' || booking?.payment_status === 'paid'
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
              >
                <CreditCard className="w-5 h-5" strokeWidth={2} />
                <span className="text-xs">{booking?.payment_status === 'paid' ? 'مدفوع ✓' : 'دفع الفاتورة'}</span>
              </button>
            )}
            <button
              onClick={() => setShowBulkUpdateModal(true)}
              disabled={booking?.status === 'cancelled'}
              className={`group p-3 rounded-xl font-medium flex flex-col items-center gap-1.5 transition-all ${booking?.status === 'cancelled'
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-white border border-[#1E293B]/20 text-[#1E293B] hover:bg-slate-50'
                }`}
            >
              <RefreshCw className={`w-5 h-5 ${booking?.status === 'cancelled' ? 'text-slate-400' : 'text-blue-600'}`} strokeWidth={2.5} />
              <span className="text-xs">تحديث الحالة</span>
            </button>
            <button
              onClick={() => {
                if (!isPaid) return showPaymentRequiredToast();
                setShowSamplesModal(true);
              }}
              disabled={!isPaid || booking?.status === 'cancelled'}
              className={`group p-3 rounded-xl font-medium flex flex-col items-center gap-1.5 transition-all ${isPaid && booking?.status !== 'cancelled'
                ? 'bg-white border border-[#1E293B]/20 text-[#1E293B] hover:bg-slate-50'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
            >
              <Layers className={`w-5 h-5 ${booking?.status === 'cancelled' ? 'text-slate-400' : 'text-blue-600'}`} strokeWidth={2.5} />
              <span className="text-xs">العينات</span>
            </button>
            <button
              onClick={() => {
                if (allTests.length === 0 || allPackages.length === 0) {
                  fetchTestsAndPackages();
                }
                setShowManageTestsModal(true);
              }}
              disabled={booking?.status === 'cancelled'}
              className={`group p-3 rounded-xl font-medium flex flex-col items-center gap-1.5 transition-all ${booking?.status === 'cancelled'
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-white border border-[#1E293B]/20 text-[#1E293B] hover:bg-slate-50'
                }`}
            >
              <Edit className={`w-5 h-5 ${booking?.status === 'cancelled' ? 'text-slate-400' : 'text-blue-600'}`} strokeWidth={2.5} />
              <span className="text-xs">إدارة التحاليل</span>
            </button>
            <button
              onClick={() => setShowCancelModal(true)}
              disabled={booking?.status === 'cancelled'}
              className={`group p-3 rounded-xl font-medium flex flex-col items-center gap-1.5 transition-all ${booking?.status === 'cancelled'
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300'
                }`}
            >
              <XCircle className="w-5 h-5" strokeWidth={2} />
              <span className="text-xs">إلغاء الحجز</span>
            </button>
          </div>

          {/* Print Actions */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">خيارات الطباعة</p>
            <div className="flex flex-wrap gap-2">
              {/* Contractor bookings are settled externally (account statement + bank transfer);
                  they never generate a ZATCA invoice, so the button is hidden for them. */}
              {!isContractorBooking && (
                <button
                  onClick={() => {
                    if (!isPaid) return showPaymentRequiredToast();
                    navigate(`/bookings/${id}/invoice`);
                  }}
                  disabled={!isPaid}
                  className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${isPaid ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-slate-50 text-slate-400 cursor-not-allowed'
                    }`}
                >
                  <Receipt className="w-3.5 h-3.5" strokeWidth={2} />
                  الفاتورة
                </button>
              )}
              <button
                onClick={() => {
                  if (!isPaid) return showPaymentRequiredToast();
                  handlePrint('Stickers');
                }}
                disabled={!isPaid}
                className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${isPaid ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-slate-50 text-slate-400 cursor-not-allowed'
                  }`}
              >
                <Barcode className="w-3.5 h-3.5" strokeWidth={2} />
                ملصقات
              </button>
              <button
                onClick={() => {
                  if (!isPaid) return showPaymentRequiredToast();
                  navigate(`/bookings/${id}/work-request`);
                }}
                disabled={!isPaid}
                className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${isPaid ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-slate-50 text-slate-400 cursor-not-allowed'
                  }`}
              >
                <FileText className="w-3.5 h-3.5" strokeWidth={2} />
                طلب العمل
              </button>
              <button
                onClick={() => {
                  setCustomerNotes(booking?.customer_notes || '');
                  setShowCustomerNotesModal(true);
                }}
                className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium flex items-center gap-1.5 hover:bg-slate-200 transition-colors"
              >
                <FileText className="w-3.5 h-3.5" strokeWidth={2} />
                ملاحظات العميل
              </button>
            </div>
          </div>
        </motion.div>

        {/* Booking & Customer Info - Modern Cards */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6"
        >
          {/* Booking Information Card */}
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-slate-600" strokeWidth={2} />
                معلومات الحجز
              </h2>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <Hash className="w-4 h-4 text-slate-400" strokeWidth={2} />
                  رقم الحجز
                </div>
                <span className="font-bold text-slate-900 font-mono">{booking?.booking_no}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <Calendar className="w-4 h-4 text-slate-400" strokeWidth={2} />
                  تاريخ الحجز
                </div>
                <div className="text-left">
                  <span className="font-semibold text-slate-900 text-sm">{new Date(booking?.booking_date).toLocaleDateString('en-GB')}</span>
                  <span className="text-xs text-slate-500 block">{new Date(booking?.booking_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <MapPin className="w-4 h-4 text-slate-400" strokeWidth={2} />
                  نوع الزيارة
                </div>
                <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${booking?.visit_type === 'contractor'
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : booking?.visit_type === 'in_lab'
                    ? 'bg-sky-50 text-sky-700 border border-sky-200'
                    : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  }`}>
                  {booking?.visit_type === 'contractor' ? 'مختبر متعاقد' : booking?.visit_type === 'in_lab' ? 'في المختبر' : 'زيارة منزلية'}
                </span>
              </div>
              {booking?.visit_type === 'home_visit' && (
                <div className="flex items-start justify-between gap-3 p-3 bg-emerald-50/60 rounded-xl border border-emerald-100">
                  <div className="flex items-center gap-2 text-slate-700 text-sm">
                    <MapPin className="w-4 h-4 text-emerald-600" strokeWidth={2} />
                    عنوان الزيارة
                  </div>
                  <span className="font-medium text-slate-900 text-sm text-left max-w-[70%] break-words">
                    {booking?.home_visit_address || 'غير محدد'}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <CreditCard className="w-4 h-4 text-slate-400" strokeWidth={2} />
                  طريقة الدفع
                </div>
                <span className="font-medium text-slate-900 text-sm capitalize">{booking?.payment_method || 'غير محدد'}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <Zap className="w-4 h-4 text-slate-400" strokeWidth={2} />
                  الاستعجال
                </div>
                <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${booking?.is_urgent ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-slate-100 text-slate-600'}`}>
                  {booking?.is_urgent ? 'عاجل' : 'عادي'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-900 rounded-xl">
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <User className="w-4 h-4" strokeWidth={2} />
                  أنشئ بواسطة
                </div>
                <span className="font-medium text-white text-sm">{booking?.created_by_user_id?.name || 'النظام'}</span>
              </div>
            </div>
          </div>

          {/* Customer Information Card */}
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4 text-slate-600" strokeWidth={2} />
                معلومات العميل
              </h2>
            </div>
            <div className="p-5">
              {/* Customer Avatar & Name */}
              <div className="flex items-center gap-3 mb-5 p-4 bg-slate-50 rounded-xl">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-lg font-bold ${customer?.gender === 'Female' ? 'bg-pink-500' : 'bg-slate-700'}`}>
                  {getCustomerName(customer)?.charAt(0) || '؟'}
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">{getCustomerName(customer)}</h3>
                  <p className="text-xs text-slate-500">{customer?.gender === 'Female' ? 'أنثى' : 'ذكر'} • {customer?.nationality || '-'}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <Hash className="w-4 h-4 text-slate-400" strokeWidth={2} />
                    رقم الهوية
                  </div>
                  <span className="font-semibold text-slate-900 text-sm font-mono">{customer?.national_id || '-'}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" strokeWidth={2} />
                    الجوال
                  </div>
                  <span className="font-semibold text-slate-900 text-sm" dir="ltr">{customer?.phone || '-'}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <Calendar className="w-4 h-4 text-slate-400" strokeWidth={2} />
                    تاريخ الميلاد
                  </div>
                  <div className="text-left">
                    <span className="font-semibold text-slate-900 text-sm">{customer?.date_of_birth ? new Date(customer.date_of_birth).toLocaleDateString('en-GB') : '-'}</span>
                    <span className="text-xs text-slate-500 block">العمر: {calculateAge(customer?.date_of_birth)} سنة</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-slate-600 text-sm">
                    <Globe className="w-4 h-4 text-slate-400" strokeWidth={2} />
                    الجنسية
                  </div>
                  <span className="font-semibold text-slate-900 text-sm">{customer?.nationality || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Contractor Delivery Status Card - Only for contractor bookings */}
        {isContractorBooking && contractorBooking && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-2xl border border-purple-200 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] overflow-hidden mb-6"
          >
            <div className="px-5 py-4 border-b border-purple-100 bg-purple-50">
              <h2 className="text-sm font-semibold text-purple-900 flex items-center gap-2">
                <Truck className="w-4 h-4 text-purple-600" strokeWidth={2} />
                حالة التوصيل - المختبر المتعاقد
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md text-xs font-medium">
                  {contractorBooking.contractor_lab_id?.name || 'غير محدد'}
                </span>
              </h2>
            </div>
            <div className="p-5">
              {/* Delivery Status Timeline */}
              <div className="flex items-center justify-between mb-6">
                {[
                  { key: 'pending', label: 'قيد الانتظار', icon: Clock },
                  { key: 'collected', label: 'تم الجمع', icon: Package },
                  { key: 'in_transit', label: 'في التوصيل', icon: Truck },
                  { key: 'processing', label: 'قيد المعالجة', icon: Activity },
                  { key: 'completed', label: 'مكتمل', icon: CheckCircle }
                ].map((step, idx, arr) => {
                  const statusOrder = ['pending', 'collected', 'in_transit', 'processing', 'completed'];
                  const currentIdx = statusOrder.indexOf(contractorBooking.status);
                  const stepIdx = statusOrder.indexOf(step.key);
                  const isActive = stepIdx <= currentIdx;
                  const isCurrent = step.key === contractorBooking.status;
                  const StepIcon = step.icon;

                  return (
                    <div key={step.key} className="flex items-center flex-1">
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isCurrent
                          ? 'bg-purple-600 text-white ring-4 ring-purple-100'
                          : isActive
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-100 text-gray-400'
                          }`}>
                          <StepIcon className="w-5 h-5" />
                        </div>
                        <span className={`text-xs mt-2 text-center font-medium ${isCurrent ? 'text-purple-700' : isActive ? 'text-purple-600' : 'text-gray-400'
                          }`}>
                          {step.label}
                        </span>
                      </div>
                      {idx < arr.length - 1 && (
                        <div className={`flex-1 h-1 mx-2 rounded ${stepIdx < currentIdx ? 'bg-purple-500' : 'bg-gray-200'
                          }`} />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Status Change Dropdown */}
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-purple-900">تغيير حالة التوصيل</p>
                  <p className="text-xs text-purple-600 mt-0.5">رقم الطلب: #{contractorBooking.booking_no}</p>
                </div>
                <select
                  value={contractorBooking.status}
                  onChange={async (e) => {
                    const newStatus = e.target.value;
                    setUpdatingDeliveryStatus(true);
                    try {
                      await contractorLabsAPI.updateBookingStatus(contractorBooking._id, { status: newStatus });
                      setContractorBooking(prev => ({ ...prev, status: newStatus }));
                      toast.success('تم تحديث حالة التوصيل بنجاح');
                    } catch (err) {
                      toast.error('حدث خطأ أثناء تحديث الحالة');
                    }
                    setUpdatingDeliveryStatus(false);
                  }}
                  disabled={updatingDeliveryStatus}
                  className="px-4 py-2 bg-white border border-purple-200 rounded-xl text-sm font-medium text-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 disabled:opacity-50"
                >
                  <option value="pending">قيد الانتظار</option>
                  <option value="collected">تم الجمع</option>
                  <option value="in_transit">في التوصيل</option>
                  <option value="processing">قيد المعالجة</option>
                  <option value="completed">مكتمل</option>
                </select>
              </div>

              {/* Contractor Lab Info */}
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">المختبر المتعاقد</p>
                  <p className="font-medium text-gray-900 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-500" />
                    {contractorBooking.contractor_lab_id?.name || '-'}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">المدينة</p>
                  <p className="font-medium text-gray-900">{contractorBooking.contractor_lab_id?.city || '-'}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Selected Tests - Modern Design */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_4px_6px_-1px_rgb(0_0_0_/_0.05)] overflow-hidden mb-6"
        >
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-slate-600" strokeWidth={2} />
              التحاليل المختارة
              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-xs font-medium">
                {booking?.tests?.length || 0}
              </span>
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">اسم التحليل</th>
                  <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">السعر</th>
                  <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">رقم العينة</th>
                  <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الحالة</th>
                  <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">ملاحظات</th>
                  <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">النتيجة</th>
                  <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.entries(testsByCategory).map(([category, tests]) => (
                  <React.Fragment key={category}>
                    {/* Category Header Row */}
                    <tr className="bg-gradient-to-l from-blue-50 to-white">
                      <td colSpan={7} className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <Folder className="w-4 h-4 text-blue-600" />
                          <span className="text-blue-700 font-bold">{category}</span>
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">
                            {tests.length} تحليل
                          </span>
                        </div>
                      </td>
                    </tr>
                    {/* Tests in this category */}
                    {tests.map((test, idx) => {
                      const packageName = test.package_id?.name_ar || test.package_id?.name_en;
                      return (
                        <React.Fragment key={test._id || idx}>
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-semibold text-gray-800 bg-blue-50 inline-block px-3 py-1.5 rounded-xl">
                                  {test.lab_test_id?.name_en || '-'}
                                </p>
                                {packageName && (
                                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                    <Package className="w-3 h-3" /> {packageName}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <p className="font-bold text-gray-800">{test.price?.toFixed(2)}</p>
                              <p className="text-xs text-gray-400">SAR</p>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <p className="font-mono text-sm bg-gray-100 inline-block px-3 py-1 rounded-lg">
                                {getPrimarySampleId(test, `${booking?.barcode_no || ''}-${String(idx + 1).padStart(2, '0')}`)}
                              </p>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase ${getStatusColor(test.status || 'pending')}`}>
                                {getStatusText(test.status || 'pending')}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors">
                                <FileText className="w-5 h-5" />
                              </button>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${test.status === 'approved' || test.status === 'released'
                                ? 'bg-green-100 text-green-700'
                                : test.status === 'cancelled'
                                  ? 'bg-rose-100 text-rose-700'
                                  : 'bg-amber-100 text-amber-700'
                                }`}>
                                {test.status === 'approved' || test.status === 'released' ? 'جاهز' :
                                  test.status === 'cancelled' ? 'ملغي' : 'قيد الانتظار'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <button
                                onClick={() => handleToggleResultEntry(test)}
                                disabled={!isPaid || booking?.status === 'cancelled'}
                                className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${!isPaid || booking?.status === 'cancelled'
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : expandedResultTest === test._id
                                    ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                    : test.status === 'result_ready' || test.status === 'approved' || test.status === 'released'
                                      ? 'bg-green-50 text-green-600 hover:bg-green-100'
                                      : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                  }`}
                              >
                                {expandedResultTest === test._id
                                  ? 'إغلاق'
                                  : test.status === 'result_ready' || test.status === 'approved' || test.status === 'released'
                                    ? 'تعديل النتيجة'
                                    : 'إضافة نتيجة'
                                }
                              </button>
                            </td>
                          </tr>
                          {/* Inline Result Entry Row */}
                          {expandedResultTest === test._id && (
                            <tr className="bg-gradient-to-l from-blue-50 to-white">
                              <td colSpan={7} className="px-6 py-4">
                                <div className="bg-white rounded-2xl border border-blue-200 p-6 shadow-sm">
                                  {loadingResult ? (
                                    <div className="flex items-center justify-center py-8">
                                      <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
                                      <span className="mr-2 text-gray-500">جاري التحميل...</span>
                                    </div>
                                  ) : (
                                    <div className="space-y-4">
                                      {test.lab_test_id?.is_serial_test && serialEntry.length > 0 && (
                                        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
                                          <h3 className="font-bold text-slate-800">نتائج التحليل المتسلسل</h3>
                                          {serialEntry.map((row, rowIdx) => (
                                            <div key={`${row.label}-${rowIdx}`} className="border border-slate-200 rounded-xl p-3">
                                              <div className="flex flex-wrap items-center gap-3 mb-3">
                                                <span className="px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-semibold">{row.label}</span>
                                                {row.sample_id && <span className="text-xs font-mono text-slate-500">{row.sample_id}</span>}
                                                <input
                                                  type="datetime-local"
                                                  value={row.sample_collected_at ? new Date(row.sample_collected_at).toISOString().slice(0, 16) : ''}
                                                  onChange={(e) => {
                                                    setSerialEntry((prev) => prev.map((x, i) => (i === rowIdx ? { ...x, sample_collected_at: e.target.value } : x)));
                                                  }}
                                                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                                                />
                                              </div>
                                              <div className="space-y-2">
                                                {(row.components || []).map((comp, cIdx) => (
                                                  <div key={`${rowIdx}-${cIdx}`} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
                                                    <span className="text-sm text-slate-700">{comp.component_name}</span>
                                                    {comp.result_type === 'single_select' || comp.result_type === 'multi_select' ? (
                                                      <select
                                                        value={comp.value || ''}
                                                        onChange={(e) => {
                                                          setSerialEntry((prev) => prev.map((x, i) => {
                                                            if (i !== rowIdx) return x;
                                                            const comps = [...x.components];
                                                            comps[cIdx] = { ...comps[cIdx], value: e.target.value };
                                                            return { ...x, components: comps };
                                                          }));
                                                        }}
                                                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                                                      >
                                                        <option value="">اختر...</option>
                                                        {(comp.options || []).map((opt, i) => (
                                                          <option key={i} value={opt}>{opt}</option>
                                                        ))}
                                                      </select>
                                                    ) : (
                                                      <input
                                                        type={comp.result_type === 'numeric' ? 'number' : 'text'}
                                                        value={comp.value || ''}
                                                        onChange={(e) => {
                                                          setSerialEntry((prev) => prev.map((x, i) => {
                                                            if (i !== rowIdx) return x;
                                                            const comps = [...x.components];
                                                            comps[cIdx] = { ...comps[cIdx], value: e.target.value };
                                                            return { ...x, components: comps };
                                                          }));
                                                        }}
                                                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                                      />
                                                    )}
                                                    <span className="text-xs text-slate-500">{comp.unit || '-'}</span>
                                                    <span className="text-xs text-slate-400">{comp.reference_range ? `${comp.reference_range.low_value} - ${comp.reference_range.high_value}` : '-'}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {resultComponents.some((c) => c.result_type === 'culture_grid') && (
                                        <div className="bg-white rounded-xl border border-emerald-200 p-4 space-y-3">
                                          <div className="flex items-center justify-between">
                                            <h3 className="font-bold text-emerald-800">Culture Grid</h3>
                                            <button type="button" onClick={addCultureRow} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs">+ إضافة Organism</button>
                                          </div>
                                          {cultureRows.map((row, rowIdx) => (
                                            <div key={rowIdx} className="border border-emerald-100 rounded-lg p-3">
                                              <div className="mb-2">
                                                <select
                                                  value={row.organism_name || ''}
                                                  onChange={(e) => setCultureRows((prev) => prev.map((x, i) => (i === rowIdx ? { ...x, organism_name: e.target.value } : x)))}
                                                  className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm"
                                                >
                                                  <option value="">اختر Organism</option>
                                                  {cultureOrganisms.map((o) => <option key={o._id} value={o.name}>{o.name}</option>)}
                                                </select>
                                              </div>
                                              <div className="space-y-2">
                                                {(row.antibiotics || []).map((ab, abIdx) => (
                                                  <div key={abIdx} className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                                    <select
                                                      value={ab.antibiotic_name || ''}
                                                      onChange={(e) => setCultureRows((prev) => prev.map((x, i) => {
                                                        if (i !== rowIdx) return x;
                                                        const antibiotics = [...(x.antibiotics || [])];
                                                        antibiotics[abIdx] = { ...antibiotics[abIdx], antibiotic_name: e.target.value };
                                                        return { ...x, antibiotics };
                                                      }))}
                                                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                                    >
                                                      <option value="">Antibiotic</option>
                                                      {cultureAntibiotics.map((a) => <option key={a._id} value={a.name}>{a.name}</option>)}
                                                    </select>
                                                    <select
                                                      value={ab.sensitivity || 'S'}
                                                      onChange={(e) => setCultureRows((prev) => prev.map((x, i) => {
                                                        if (i !== rowIdx) return x;
                                                        const antibiotics = [...(x.antibiotics || [])];
                                                        antibiotics[abIdx] = { ...antibiotics[abIdx], sensitivity: e.target.value };
                                                        return { ...x, antibiotics };
                                                      }))}
                                                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                                    >
                                                      <option value="S">Sensitive (S)</option>
                                                      <option value="I">Intermediate (I)</option>
                                                      <option value="R">Resistant (R)</option>
                                                    </select>
                                                    <input
                                                      value={ab.mic_value || ''}
                                                      onChange={(e) => setCultureRows((prev) => prev.map((x, i) => {
                                                        if (i !== rowIdx) return x;
                                                        const antibiotics = [...(x.antibiotics || [])];
                                                        antibiotics[abIdx] = { ...antibiotics[abIdx], mic_value: e.target.value };
                                                        return { ...x, antibiotics };
                                                      }))}
                                                      placeholder="MIC"
                                                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                                    />
                                                  </div>
                                                ))}
                                                <button type="button" onClick={() => addCultureAntibiotic(rowIdx)} className="px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-lg text-xs">
                                                  + إضافة Antibiotic
                                                </button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {resultComponents.some((c) => c.result_type === 'rich_text_with_images') && (
                                        <div className="bg-white rounded-xl border border-violet-200 p-4 space-y-3">
                                          <h3 className="font-bold text-violet-800">Rich Text Report</h3>
                                          <div className="flex gap-2">
                                            {['bold', 'italic', 'insertUnorderedList', 'insertOrderedList'].map((cmd) => (
                                              <button key={cmd} type="button" onClick={() => document.execCommand(cmd)} className="px-2 py-1 border border-slate-200 rounded text-xs">
                                                {cmd}
                                              </button>
                                            ))}
                                          </div>
                                          <div
                                            contentEditable
                                            suppressContentEditableWarning
                                            className="min-h-[140px] border border-slate-200 rounded-lg p-3 text-sm"
                                            onInput={(e) => setRichTextHtml(e.currentTarget.innerHTML)}
                                            dangerouslySetInnerHTML={{ __html: richTextHtml || '' }}
                                          />
                                          <input type="file" multiple accept="image/*" onChange={(e) => onRichImageUpload(e.target.files)} />
                                          {richTextImages.length > 0 && (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                              {richTextImages.map((img, idx) => (
                                                <img key={idx} src={img.url} alt={img.caption || `img-${idx}`} className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {resultComponents.filter((comp) => !['culture_grid', 'rich_text_with_images'].includes(comp.result_type)).length > 0 &&
                                        resultComponents.filter((comp) => !['culture_grid', 'rich_text_with_images'].includes(comp.result_type)).map((comp, compIdx) => {
                                        const abnormalFlag = calculateAbnormalFlag(comp.value, comp.reference_range);
                                        const abnormalLabel = getAbnormalLabel(abnormalFlag);

                                        return (
                                          <div key={compIdx} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                            <div className="flex items-center justify-between mb-3">
                                              <h4 className="font-bold text-blue-700">{comp.component_name}</h4>
                                              {abnormalLabel && (
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${abnormalLabel.color}`}>
                                                  {abnormalLabel.text}
                                                </span>
                                              )}
                                            </div>

                                            <div className="flex items-center gap-4">
                                              <button
                                                onClick={() => {
                                                  const normalIndices = resultComponents
                                                    .map((c, i) => (!['culture_grid', 'rich_text_with_images'].includes(c.result_type) ? i : -1))
                                                    .filter((x) => x !== -1);
                                                  const sourceIndex = normalIndices[compIdx];
                                                  if (sourceIndex === undefined) return;
                                                  const updated = [...resultComponents];
                                                  updated[sourceIndex].value = '';
                                                  setResultComponents(updated);
                                                }}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                              >
                                                <X className="w-5 h-5" />
                                              </button>

                                              {comp.unit && (
                                                <span className="text-gray-500 font-medium">{comp.unit}</span>
                                              )}

                                              <div className="flex-1 relative">
                                                {comp.result_type === 'single_select' || comp.result_type === 'multi_select' ? (
                                                  <select
                                                    value={comp.value || ''}
                                                    onChange={(e) => {
                                                      const normalIndices = resultComponents
                                                        .map((c, i) => (!['culture_grid', 'rich_text_with_images'].includes(c.result_type) ? i : -1))
                                                        .filter((x) => x !== -1);
                                                      const sourceIndex = normalIndices[compIdx];
                                                      if (sourceIndex === undefined) return;
                                                      const updated = [...resultComponents];
                                                      updated[sourceIndex].value = e.target.value;
                                                      updated[sourceIndex].abnormal_flag = calculateAbnormalFlag(e.target.value, comp.reference_range);
                                                      setResultComponents(updated);
                                                    }}
                                                    disabled={comp.is_calculated}
                                                    className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg text-left appearance-none ${comp.is_calculated ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}
                                                    dir="ltr"
                                                  >
                                                    <option value="">Select option</option>
                                                    {(comp.options || []).map((opt, i) => (
                                                      <option key={i} value={opt}>{opt}</option>
                                                    ))}
                                                  </select>
                                                ) : (
                                                  <input
                                                    type={comp.result_type === 'numeric' ? 'number' : 'text'}
                                                    value={comp.value || ''}
                                                    onChange={(e) => {
                                                      const normalIndices = resultComponents
                                                        .map((c, i) => (!['culture_grid', 'rich_text_with_images'].includes(c.result_type) ? i : -1))
                                                        .filter((x) => x !== -1);
                                                      const sourceIndex = normalIndices[compIdx];
                                                      if (sourceIndex === undefined) return;
                                                      const updated = [...resultComponents];
                                                      updated[sourceIndex].value = e.target.value;
                                                      updated[sourceIndex].abnormal_flag = calculateAbnormalFlag(e.target.value, comp.reference_range);
                                                      setResultComponents(updated);
                                                    }}
                                                    disabled={comp.is_calculated}
                                                    placeholder={comp.is_calculated ? 'يتم حسابه آلياً' : 'Enter value'}
                                                    className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg text-left ${comp.is_calculated ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}
                                                    dir="ltr"
                                                  />
                                                )}

                                                {comp.is_calculated && (
                                                  <div className="absolute left-3 top-3.5 text-blue-500" title="قيمة محسوبة آلياً">
                                                    <Activity className="w-5 h-5" />
                                                  </div>
                                                )}
                                              </div>
                                            </div>

                                            {comp.reference_range && (
                                              <div className="mt-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                                                <div className="flex items-center gap-2 text-blue-700">
                                                  <Check className="w-4 h-4" />
                                                  <span className="font-medium">REFERENCE RANGE:</span>
                                                </div>
                                                <p className="text-blue-800 font-semibold mt-1">
                                                  {comp.reference_range.gender !== 'Any' && `${comp.reference_range.gender}: `}
                                                  {comp.reference_range.low_value} - {comp.reference_range.high_value} {comp.unit}
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}

                                      <div className="bg-white rounded-xl border border-slate-200 p-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">ملاحظات إضافية (اختياري)</label>
                                        <textarea
                                          value={resultComment}
                                          onChange={(e) => setResultComment(e.target.value)}
                                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                          rows={2}
                                          placeholder="أي ملاحظات على النتيجة..."
                                        />
                                      </div>

                                      <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                                        <button
                                          onClick={() => handleSaveResult(test._id)}
                                          disabled={savingResult}
                                          className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                                        >
                                          {savingResult ? (
                                            <RefreshCw className="w-5 h-5 animate-spin" />
                                          ) : (
                                            <Save className="w-5 h-5" />
                                          )}
                                          Save Changes
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                ))}
                {Object.keys(testsByCategory).length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <FlaskConical className="w-12 h-12 text-gray-300" />
                        <p className="text-gray-500">لا توجد تحاليل في هذا الحجز</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* Complex Result Entry Modal (Serial / Culture / Rich Text) */}
      {showComplexResultModal && expandedResultTestData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowComplexResultModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#1E293B] px-6 py-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                  <FlaskConical className="w-5 h-5" />
                  إدخال نتيجة التحليل
                </h3>
                <p className="text-slate-300 text-sm mt-1">
                  {expandedResultTestData.lab_test_id?.name_en || '-'} - {booking?.barcode_no || '-'}
                </p>
              </div>
              <button onClick={() => setShowComplexResultModal(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-5 h-5 text-slate-100" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {loadingResult ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
                  <span className="mr-2 text-gray-500">جاري التحميل...</span>
                </div>
              ) : (
                <>
                  {expandedResultTestData?.lab_test_id?.is_serial_test && serialEntry.length > 0 && (
                    <div className="space-y-4">
                      {/* Shared info components - entered once */}
                      {sharedComponents.length > 0 && (
                        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                          <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            <Folder className="w-4 h-4 text-slate-500" />
                            معلومات التحليل
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {sharedComponents.map((comp, cIdx) => (
                              <div key={`shared-${cIdx}`} className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-slate-600">{comp.component_name}</label>
                                {comp.result_type === 'single_select' || comp.result_type === 'multi_select' ? (
                                  <select
                                    value={comp.value || ''}
                                    onChange={(e) => {
                                      setSharedComponents(prev => prev.map((x, i) => i === cIdx ? { ...x, value: e.target.value } : x));
                                    }}
                                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                                  >
                                    <option value="">اختر...</option>
                                    {(comp.options || []).map((opt, i) => (
                                      <option key={i} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    type="text"
                                    value={comp.value || ''}
                                    onChange={(e) => {
                                      setSharedComponents(prev => prev.map((x, i) => i === cIdx ? { ...x, value: e.target.value } : x));
                                    }}
                                    placeholder={comp.unit || ''}
                                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Start time picker with auto-fill */}
                      <div className="bg-white rounded-xl border border-indigo-200 p-4 space-y-3">
                        <h3 className="font-bold text-indigo-800 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          وقت بدء التحليل
                        </h3>
                        <div className="flex flex-wrap items-end gap-3">
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-slate-500">التاريخ</label>
                            <input
                              type="date"
                              value={serialStartTime ? serialStartTime.slice(0, 10) : ''}
                              onChange={(e) => {
                                const timeVal = serialStartTime ? serialStartTime.slice(11, 16) : '';
                                handleSerialStartTimeChange(e.target.value, timeVal || '00:00');
                              }}
                              className="px-3 py-2 border border-indigo-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-slate-500">الساعة</label>
                            <input
                              type="time"
                              value={serialStartTime ? serialStartTime.slice(11, 16) : ''}
                              onChange={(e) => {
                                const dateVal = serialStartTime ? serialStartTime.slice(0, 10) : new Date().toISOString().slice(0, 10);
                                handleSerialStartTimeChange(dateVal, e.target.value);
                              }}
                              className="px-3 py-2 border border-indigo-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </div>
                          {serialStartTime && (
                            <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded-md">
                              سيتم حساب أوقات الفترات تلقائيا
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Serial measurement intervals */}
                      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                          <Activity className="w-4 h-4 text-slate-500" />
                          نتائج القياسات
                        </h3>
                        <div className="space-y-2">
                          {serialEntry.map((row, rowIdx) => (
                            <div key={`${row.label}-${rowIdx}`} className="border border-slate-200 rounded-xl overflow-hidden">
                              {/* Interval header */}
                              <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                                <span className="px-2.5 py-1 rounded-md bg-indigo-100 text-indigo-700 text-xs font-bold">{row.label}</span>
                                {row.sample_id && <span className="text-xs font-mono text-slate-400">{row.sample_id}</span>}
                                {row.sample_collected_at ? (
                                  <span className="text-xs font-medium text-slate-600 bg-white border border-slate-200 px-2 py-1 rounded-md flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {formatIntervalTime(row.sample_collected_at)}
                                  </span>
                                ) : null}
                                {/* Editable time override */}
                                <input
                                  type="time"
                                  value={row.sample_collected_at ? new Date(row.sample_collected_at).toISOString().slice(11, 16) : ''}
                                  onChange={(e) => {
                                    setSerialEntry(prev => prev.map((x, i) => {
                                      if (i !== rowIdx) return x;
                                      const dateBase = x.sample_collected_at ? new Date(x.sample_collected_at).toISOString().slice(0, 10) : (serialStartTime ? serialStartTime.slice(0, 10) : new Date().toISOString().slice(0, 10));
                                      return { ...x, sample_collected_at: e.target.value ? `${dateBase}T${e.target.value}` : '' };
                                    }));
                                  }}
                                  className="px-2 py-1 border border-slate-200 rounded-md text-xs w-24 ml-auto"
                                  title="تعديل الوقت"
                                />
                              </div>
                              {/* Measurement components */}
                              <div className="px-4 py-3 space-y-2">
                                {(row.components || []).map((comp, cIdx) => (
                                  <div key={`${rowIdx}-${cIdx}`} className="grid grid-cols-[1fr_120px_60px_100px] gap-2 items-center">
                                    <span className="text-sm text-slate-700 font-medium">{comp.component_name}</span>
                                    {comp.result_type === 'single_select' || comp.result_type === 'multi_select' ? (
                                      <select
                                        value={comp.value || ''}
                                        onChange={(e) => {
                                          setSerialEntry((prev) => prev.map((x, i) => {
                                            if (i !== rowIdx) return x;
                                            const comps = [...x.components];
                                            comps[cIdx] = { ...comps[cIdx], value: e.target.value };
                                            return { ...x, components: comps };
                                          }));
                                        }}
                                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                                      >
                                        <option value="">اختر...</option>
                                        {(comp.options || []).map((opt, i) => (
                                          <option key={i} value={opt}>{opt}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <input
                                        type={comp.result_type === 'numeric' ? 'number' : 'text'}
                                        value={comp.value || ''}
                                        onChange={(e) => {
                                          const newValue = e.target.value;
                                          setSerialEntry((prev) => prev.map((x, i) => {
                                            if (i !== rowIdx) return x;
                                            const comps = [...x.components];
                                            const abnormal = comp.result_type === 'numeric' ? calculateAbnormalFlag(newValue, comp.reference_range) : null;
                                            comps[cIdx] = { ...comps[cIdx], value: newValue, abnormal_flag: abnormal };
                                            return { ...x, components: comps };
                                          }));
                                        }}
                                        className={`px-3 py-2 border rounded-lg text-sm text-center ${
                                          comp.abnormal_flag === 'H' || comp.abnormal_flag === 'L'
                                            ? 'border-red-300 bg-red-50'
                                            : 'border-slate-200'
                                        }`}
                                      />
                                    )}
                                    <span className="text-xs text-slate-500 text-center">{comp.unit || '-'}</span>
                                    <span className="text-xs text-slate-400 text-center">
                                      {comp.reference_range ? `${comp.reference_range.low_value ?? ''} - ${comp.reference_range.high_value ?? ''}` : '-'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {resultComponents.some((c) => c.result_type === 'culture_grid') && (
                    <div className="bg-white rounded-xl border border-emerald-200 p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-emerald-800">Culture Grid</h3>
                        <button type="button" onClick={addCultureRow} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs">+ إضافة Organism</button>
                      </div>
                      {cultureRows.map((row, rowIdx) => (
                        <div key={rowIdx} className="border border-emerald-100 rounded-lg p-3">
                          <div className="mb-2">
                            <select
                              value={row.organism_name || ''}
                              onChange={(e) => setCultureRows((prev) => prev.map((x, i) => (i === rowIdx ? { ...x, organism_name: e.target.value } : x)))}
                              className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm"
                            >
                              <option value="">اختر Organism</option>
                              {cultureOrganisms.map((o) => <option key={o._id} value={o.name}>{o.name}</option>)}
                            </select>
                          </div>
                          <div className="space-y-2">
                            {(row.antibiotics || []).map((ab, abIdx) => (
                              <div key={abIdx} className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                <select
                                  value={ab.antibiotic_name || ''}
                                  onChange={(e) => setCultureRows((prev) => prev.map((x, i) => {
                                    if (i !== rowIdx) return x;
                                    const antibiotics = [...(x.antibiotics || [])];
                                    antibiotics[abIdx] = { ...antibiotics[abIdx], antibiotic_name: e.target.value };
                                    return { ...x, antibiotics };
                                  }))}
                                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                >
                                  <option value="">Antibiotic</option>
                                  {cultureAntibiotics.map((a) => <option key={a._id} value={a.name}>{a.name}</option>)}
                                </select>
                                <select
                                  value={ab.sensitivity || 'S'}
                                  onChange={(e) => setCultureRows((prev) => prev.map((x, i) => {
                                    if (i !== rowIdx) return x;
                                    const antibiotics = [...(x.antibiotics || [])];
                                    antibiotics[abIdx] = { ...antibiotics[abIdx], sensitivity: e.target.value };
                                    return { ...x, antibiotics };
                                  }))}
                                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                >
                                  <option value="S">Sensitive (S)</option>
                                  <option value="I">Intermediate (I)</option>
                                  <option value="R">Resistant (R)</option>
                                </select>
                                <input
                                  value={ab.mic_value || ''}
                                  onChange={(e) => setCultureRows((prev) => prev.map((x, i) => {
                                    if (i !== rowIdx) return x;
                                    const antibiotics = [...(x.antibiotics || [])];
                                    antibiotics[abIdx] = { ...antibiotics[abIdx], mic_value: e.target.value };
                                    return { ...x, antibiotics };
                                  }))}
                                  placeholder="MIC"
                                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                                />
                              </div>
                            ))}
                            <button type="button" onClick={() => addCultureAntibiotic(rowIdx)} className="px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-lg text-xs">
                              + إضافة Antibiotic
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {resultComponents.some((c) => c.result_type === 'rich_text_with_images') && (
                    <div className="bg-white rounded-xl border border-violet-200 p-4 space-y-3">
                      <h3 className="font-bold text-violet-800">Rich Text Report</h3>
                      <div className="flex gap-2">
                        {['bold', 'italic', 'insertUnorderedList', 'insertOrderedList'].map((cmd) => (
                          <button key={cmd} type="button" onClick={() => document.execCommand(cmd)} className="px-2 py-1 border border-slate-200 rounded text-xs">
                            {cmd}
                          </button>
                        ))}
                      </div>
                      <div
                        contentEditable
                        suppressContentEditableWarning
                        className="min-h-[140px] border border-slate-200 rounded-lg p-3 text-sm"
                        onInput={(e) => setRichTextHtml(e.currentTarget.innerHTML)}
                        dangerouslySetInnerHTML={{ __html: richTextHtml || '' }}
                      />
                      <input type="file" multiple accept="image/*" onChange={(e) => onRichImageUpload(e.target.files)} />
                      {richTextImages.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {richTextImages.map((img, idx) => (
                            <img key={idx} src={img.url} alt={img.caption || `img-${idx}`} className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">ملاحظات إضافية (اختياري)</label>
                    <textarea
                      value={resultComment}
                      onChange={(e) => setResultComment(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      rows={2}
                      placeholder="أي ملاحظات على النتيجة..."
                    />
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => setShowComplexResultModal(false)}
                className="px-5 py-2.5 text-slate-700 bg-white border border-slate-200 rounded-xl font-medium hover:bg-slate-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={() => handleSaveResult(expandedResultTestData?._id)}
                disabled={savingResult || loadingResult}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
              >
                {savingResult ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2.5} />}
                حفظ النتيجة
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold">تأكيد إلغاء الحجز</h3>
              <p className="text-gray-500 mt-2">هل أنت متأكد من إلغاء هذا الحجز؟ لا يمكن تراجع عن هذا الإجراء.</p>

              {isPaid && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 text-right">
                  <strong>⚠️ تنبيه هام:</strong> الحجز مدفوع. سيتم إصدار إشعار دائن (Credit Note) وإرساله لهيئة الزكاة تلقائياً لتوثيق الاسترجاع.
                </div>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 text-right">سبب الإلغاء <span className="text-red-500">*</span></label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="يرجى كتابة سبب الإلغاء..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 text-right resize-none h-24"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                disabled={isCancelling}
                className="flex-1 px-6 py-3 bg-white border border-[#1E293B]/20 text-[#1E293B] rounded-xl font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                تراجع
              </button>
              <button
                onClick={handleCancelBooking}
                disabled={!cancelReason.trim() || isCancelling}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCancelling ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    جاري الإلغاء...
                  </>
                ) : (
                  'إلغاء الحجز'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl"
          >
            {booking?.payment_status === 'paid' && !forceEditPayment ? (
              // Already Paid - Show confirmation message
              <>
                <div className="bg-[#1E293B] px-6 py-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-slate-100 flex items-center gap-3">
                      <div className="p-2 bg-white/10 rounded-xl">
                        <Check className="w-6 h-6" />
                      </div>
                      تم الدفع
                    </h3>
                    <button onClick={() => {
                      setShowPaymentModal(false);
                      setForceEditPayment(false);
                    }} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                      <X className="w-5 h-5 text-slate-100" />
                    </button>
                  </div>
                </div>
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">تم تأكيد الدفع بنجاح</h4>
                  <p className="text-gray-600 mb-4">
                    المبلغ المدفوع: <span className="font-bold text-green-600">{booking?.paid_amount?.toFixed(2)} ر.س</span>
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <p className="text-sm text-amber-800 font-medium">⚠️ لا يمكن تعديل الفاتورة بعد إصدارها (متطلب هيئة الزكاة والضريبة)</p>
                    <p className="text-xs text-amber-600 mt-1">في حالة وجود خطأ، يرجى إلغاء الحجز وإنشاء حجز جديد.</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowPaymentModal(false);
                      setForceEditPayment(false);
                    }}
                    className="w-full px-6 py-3 bg-[#1E293B] text-white rounded-xl font-medium hover:bg-[#334155] transition-colors"
                  >
                    إغلاق
                  </button>
                </div>
              </>
            ) : (
              // Not Paid Yet - Show payment form
              <>
                <div className="bg-[#1E293B] px-6 py-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-slate-100 flex items-center gap-3">
                      <div className="p-2 bg-white/10 rounded-xl">
                        <CreditCard className="w-6 h-6" />
                      </div>
                      معالجة الدفع
                    </h3>
                    <button onClick={() => setShowPaymentModal(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                      <X className="w-5 h-5 text-slate-100" />
                    </button>
                  </div>
                  {/* Amount Summary */}
                  <div className="mt-4 p-4 bg-white/10 rounded-xl space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-white/80">المبلغ الأصلي</span>
                      <span className="text-lg font-semibold text-white">{booking?.net_amount?.toFixed(2)} SAR</span>
                    </div>
                    {discountAmount > 0 && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-white/80">الخصم ({appliedCoupon?.code})</span>
                          <span className="text-lg font-semibold text-green-300">-{discountAmount.toFixed(2)} SAR</span>
                        </div>
                        <div className="pt-2 border-t border-white/20 flex justify-between items-center">
                          <span className="text-white/90 font-medium">المبلغ بعد الخصم</span>
                          <span className="text-2xl font-bold text-white">
                            {((booking?.net_amount || 0) - discountAmount).toFixed(2)} SAR
                          </span>
                        </div>
                      </>
                    )}
                    {discountAmount === 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-white/80">المبلغ المطلوب</span>
                        <span className="text-2xl font-bold text-white">{booking?.net_amount?.toFixed(2)} SAR</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-6 space-y-5">
                  {/* Coupon Code */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Ticket className="w-4 h-4 text-blue-600" />
                      كود الخصم
                    </label>
                    {appliedCoupon ? (
                      <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-green-700">{appliedCoupon.code}</span>
                            <span className="text-sm text-green-600">
                              ({appliedCoupon.type === 'fixed' ? `${appliedCoupon.value} ر.س` : `${appliedCoupon.value}%`})
                            </span>
                          </div>
                          <p className="text-xs text-green-600 mt-1">{appliedCoupon.name}</p>
                        </div>
                        <button
                          onClick={handleRemoveCoupon}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="إزالة الكوبون"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="أدخل كود الخصم"
                          disabled={applyingCoupon}
                          className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all disabled:bg-gray-50 uppercase"
                        />
                        <button
                          onClick={handleApplyCoupon}
                          disabled={applyingCoupon || !couponCode.trim()}
                          className="px-5 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {applyingCoupon ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          ) : (
                            'تطبيق'
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Payment Method */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-blue-600" />
                      طريقة الدفع
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'cash', label: 'نقدي', icon: '💵' },
                        { value: 'card', label: 'بطاقة', icon: '💳' },
                        { value: 'cash_card', label: 'نقد + بطاقة', icon: '💰' },
                        { value: 'insurance', label: 'تأمين', icon: '🏥' },
                        { value: 'tamara', label: 'تمارا', icon: '📱' },
                        { value: 'tabby', label: 'تابي', icon: '📲' },
                      ].map(method => (
                        <button
                          key={method.value}
                          onClick={() => {
                            setPaymentMethod(method.value);
                            if (method.value === 'cash_card') {
                              setCashAmount(0);
                              setCardAmount(0);
                            }
                          }}
                          className={`p-3 rounded-xl border-2 text-center transition-all ${paymentMethod === method.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <span className="text-xl block mb-1">{method.icon}</span>
                          <span className="text-sm font-medium">{method.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Cash + Card Amounts */}
                  {paymentMethod === 'cash_card' && (
                    <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-xl">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">مبلغ نقدي</label>
                        <input
                          type="number"
                          value={cashAmount}
                          onChange={(e) => setCashAmount(parseFloat(e.target.value) || 0)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">مبلغ بطاقة</label>
                        <input
                          type="number"
                          value={cardAmount}
                          onChange={(e) => setCardAmount(parseFloat(e.target.value) || 0)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                      <div className="col-span-2 text-center pt-2 border-t border-blue-200">
                        <span className="text-sm text-gray-600">المجموع: </span>
                        <span className={`font-bold ${cashAmount + cardAmount >= (booking?.net_amount || 0) ? 'text-green-600' : 'text-red-500'}`}>
                          {(cashAmount + cardAmount).toFixed(2)} SAR
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-6 py-4 bg-slate-50/50 flex gap-3">
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="flex-1 px-6 py-3 bg-white border border-[#1E293B]/20 text-[#1E293B] rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleProcessPayment}
                    disabled={processingPayment || !paymentMethod}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingPayment ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        تأكيد الدفع
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}

      {/* Customer Notes Modal */}
      {showCustomerNotesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl"
          >
            <div className="bg-[#1E293B] px-6 py-5 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-100 flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-xl">
                  <FileText className="w-5 h-5" />
                </div>
                ملاحظات العميل
              </h3>
              <button onClick={() => setShowCustomerNotesModal(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-5 h-5 text-slate-100" />
              </button>
            </div>
            <div className="p-6">
              <textarea
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                placeholder="أدخل ملاحظات العميل هنا..."
                rows={5}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
              />
            </div>
            <div className="px-6 py-4 bg-slate-50/50 flex gap-3">
              <button
                onClick={() => setShowCustomerNotesModal(false)}
                className="flex-1 px-6 py-3 bg-white border border-[#1E293B]/20 text-[#1E293B] rounded-xl font-medium hover:bg-slate-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleSaveCustomerNotes}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors"
              >
                <Save className="w-5 h-5" />
                حفظ
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Manage Tests Modal */}
      {showManageTestsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                <FlaskConical className="w-5 h-5" />
                Manage Tests
              </h3>
              <button onClick={() => setShowManageTestsModal(false)} className="text-slate-400 hover:text-slate-100 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {/* Search */}
              <div className="relative mb-4">
                <input
                  type="text"
                  value={testSearch}
                  onChange={(e) => setTestSearch(e.target.value)}
                  placeholder="Search by test name..."
                  className="w-full px-4 py-3 pr-11 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
                />
                <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>

              {/* Tests List */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredTests.slice(0, 15).map(test => {
                  const isSelected = selectedNewTests.includes(test._id);
                  return (
                    <button
                      key={test._id}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedNewTests(selectedNewTests.filter(id => id !== test._id));
                        } else {
                          setSelectedNewTests([...selectedNewTests, test._id]);
                        }
                      }}
                      className={`w-full text-right px-4 py-3 rounded-xl flex items-center justify-between ${isSelected ? 'bg-blue-50 border-2 border-blue-500' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                    >
                      <div className="flex items-center gap-2">
                        {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                        <span className="font-medium">{test.name_en}</span>
                      </div>
                      <span className="text-blue-600 font-semibold">{test.price || 0} ر.س</span>
                    </button>
                  );
                })}
              </div>

              {/* Packages */}
              <h4 className="font-semibold text-gray-700 mt-6 mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                الباقات
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {allPackages.slice(0, 10).map(pkg => {
                  const isSelected = selectedNewPackages.includes(pkg._id);
                  return (
                    <button
                      key={pkg._id}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedNewPackages(selectedNewPackages.filter(id => id !== pkg._id));
                        } else {
                          setSelectedNewPackages([...selectedNewPackages, pkg._id]);
                        }
                      }}
                      className={`w-full text-right px-4 py-3 rounded-xl flex items-center justify-between ${isSelected ? 'bg-blue-50 border-2 border-blue-500' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                    >
                      <div className="flex items-center gap-2">
                        {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                        <span className="font-medium">{pkg.name}</span>
                      </div>
                      <span className="text-blue-600 font-semibold">{pkg.price || 0} ر.س</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50/50 flex gap-3">
              <button
                onClick={() => setShowManageTestsModal(false)}
                className="flex-1 px-6 py-3 bg-white border border-[#1E293B]/20 text-[#1E293B] rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleAddTests}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                Add {selectedNewTests.length + selectedNewPackages.length} Items
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Status Modal */}
      {showBulkUpdateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
          >
            <div className="bg-[#1E293B] px-6 py-5">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-100 flex items-center gap-3">
                  <div className="p-2 bg-white/10 rounded-xl">
                    <RefreshCw className="w-5 h-5" />
                  </div>
                  تحديث حالة العينات
                </h3>
                <button onClick={() => setShowBulkUpdateModal(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-slate-100" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {/* New Status Selection */}
              <div className="mb-6">
                <label className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-blue-600" />
                  الحالة الجديدة
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'pending', label: 'قيد الانتظار', color: 'amber' },
                    { value: 'collected', label: 'تم الجمع', color: 'cyan' },
                    { value: 'partially_collected', label: 'جزئي', color: 'orange' },
                    { value: 'rejected', label: 'مرفوض', color: 'red' },
                    { value: 'verified', label: 'تم التحقق', color: 'blue' },
                    { value: 'approved', label: 'معتمد', color: 'green' },
                  ].map(status => (
                    <button
                      key={status.value}
                      onClick={() => setBulkStatus(status.value)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${bulkStatus === status.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <span className={`text-sm font-medium ${bulkStatus === status.value ? 'text-blue-700' : 'text-gray-700'}`}>
                        {status.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Search and Controls */}
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <input
                    type="text"
                    placeholder="ابحث عن تحليل..."
                    className="w-full px-4 py-2.5 pr-11 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
                <div className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium">
                  {selectedTestsForUpdate.length} من {Object.values(testsByCategory).flat().length} محدد
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={selectAllVisible}
                  className="px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-xl text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
                >
                  <Check className="w-4 h-4" />
                  تحديد الكل
                </button>
                <button
                  onClick={clearSelection}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium flex items-center gap-2 hover:bg-gray-50 transition-colors"
                >
                  <X className="w-4 h-4" />
                  إلغاء التحديد
                </button>
                <button
                  onClick={() => {
                    const allExpanded = {};
                    Object.keys(testsByCategory).forEach(cat => allExpanded[cat] = true);
                    setExpandedCategories(allExpanded);
                  }}
                  className="px-4 py-2 border border-blue-200 text-blue-600 rounded-xl text-sm font-medium flex items-center gap-2 hover:bg-blue-50 transition-colors"
                >
                  <ChevronDown className="w-4 h-4" />
                  توسيع الكل
                </button>
              </div>

              {/* Categories and Tests */}
              <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                {Object.entries(testsByCategory).map(([category, tests]) => (
                  <div key={category} className="border-b last:border-0">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-1 rounded transition-transform ${expandedCategories[category] ? 'rotate-90' : ''}`}>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                        <Folder className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-gray-800">{category}</span>
                      </div>
                      <span className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full font-medium">{tests.length} تحليل</span>
                    </button>
                    {expandedCategories[category] && (
                      <div className="bg-gray-50 divide-y divide-gray-100">
                        {tests.map((test, idx) => (
                          <div key={test._id || idx} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-100 transition-colors">
                            <input
                              type="checkbox"
                              checked={selectedTestsForUpdate.includes(test._id)}
                              onChange={() => toggleTestSelection(test._id)}
                              className="w-5 h-5 rounded-lg border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-800 truncate">{test.lab_test_id?.name_en || test.package_id?.name || '-'}</p>
                              <p className="text-xs text-gray-500">
                                {getPrimarySampleId(test, `${booking?.barcode_no || ''}-${String(idx + 1).padStart(2, '0')}`)} • {test.lab_test_id?.specimen_type || '-'}
                              </p>
                            </div>
                            <span className={`px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap ${getStatusColor(test.status || 'pending')}`}>
                              {getStatusText(test.status || 'pending')}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-50/50 flex gap-3 border-t border-slate-100">
              <button
                onClick={() => setShowBulkUpdateModal(false)}
                className="flex-1 px-6 py-3 bg-white border border-[#1E293B]/20 text-[#1E293B] rounded-xl font-medium hover:bg-slate-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleBulkUpdate}
                disabled={!bulkStatus || selectedTestsForUpdate.length === 0}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                تحديث {selectedTestsForUpdate.length} تحليل
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Samples/Labels Modal */}
      {showSamplesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                <Layers className="w-5 h-5" />
                Sample Labels
              </h3>
              <button onClick={() => setShowSamplesModal(false)} className="text-slate-400 hover:text-slate-100 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {/* Selection Controls */}
              <div className="flex items-center justify-end gap-4 mb-6">
                <button
                  onClick={() => setSelectedSamples([])}
                  className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  Deselect All
                </button>
                <button
                  onClick={() => setSelectedSamples(samplesByDepartment.map(s => s.id))}
                  className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg font-medium flex items-center gap-1 hover:bg-blue-50"
                >
                  Select All <Check className="w-4 h-4" />
                </button>
              </div>

              {/* Sample Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {samplesByDepartment.map((sample) => {
                  const isSelected = selectedSamples.includes(sample.id);
                  return (
                    <div
                      key={sample.id}
                      className={`border-2 rounded-xl overflow-hidden cursor-pointer transition-all ${isSelected ? 'border-blue-500 shadow-lg' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedSamples(selectedSamples.filter(id => id !== sample.id));
                        } else {
                          setSelectedSamples([...selectedSamples, sample.id]);
                        }
                      }}
                    >
                      {/* Header with checkbox */}
                      <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => { }}
                          className="w-5 h-5 rounded border-gray-300"
                        />
                        <span className="font-mono text-sm text-gray-600">{sample.sampleId}</span>
                      </div>

                      {/* Card Content */}
                      <div className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="text-right flex-1">
                            <p className="font-bold text-gray-800">{getCustomerName(customer)}</p>
                            <p className="text-sm text-gray-500">{sample.department}</p>
                            {sample.phaseLabel && (
                              <p className="text-xs text-indigo-600 font-medium mt-1">{sample.phaseLabel}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex justify-between text-sm text-gray-600 mb-3">
                          <span>{sample.tubeType}</span>
                          <span>{sample.specimenType}</span>
                        </div>

                        {/* Barcode */}
                        <div className="bg-gray-100 rounded-lg p-4 text-center mb-3">
                          <div className="flex justify-center mb-2">
                            {/* Simple barcode representation */}
                            <div className="flex gap-0.5">
                              {[...Array(30)].map((_, i) => (
                                <div
                                  key={i}
                                  className="bg-gray-800"
                                  style={{
                                    width: Math.random() > 0.5 ? '2px' : '1px',
                                    height: '40px'
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="font-mono text-sm">{sample.sampleId}</p>
                        </div>

                        {/* Footer — show test names (one physical sample can cover multiple tests) */}
                        <div className="text-center text-sm text-blue-600 mb-1">
                          {sample.department} | {sample.testCount} test{sample.testCount > 1 ? 's' : ''}
                        </div>
                        {sample.tests?.length > 0 && (
                          <ul className="text-xs text-slate-700 space-y-0.5 text-right">
                            {sample.tests.map((t, i) => (
                              <li key={i} className="truncate">• {t}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {samplesByDepartment.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  لا توجد عينات في هذا الحجز
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-slate-50/50 flex items-center justify-between">
              <span className="text-slate-600">selected {selectedSamples.length}</span>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (selectedSamples.length === 0) {
                      toast.error('يرجى اختيار عينة واحدة على الأقل');
                      return;
                    }
                    handlePrint('Labels');
                  }}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium flex items-center gap-2 hover:bg-blue-700 transition-colors"
                >
                  Print Selected <Printer className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowSamplesModal(false)}
                  className="px-6 py-3 bg-white border border-[#1E293B]/20 text-[#1E293B] rounded-xl font-medium hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </motion.div>
  );
};

export default BookingDetails;
